import { Users, Loader2, Sword, Flame } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * PartyBar — shown at top of Dashboard when the active run has a party.
 * Displays both heroes' presence, whose turn it currently is, and an "invite" hint
 * for the host BEFORE the guest has joined.
 *
 * Props:
 *   party          — snapshot from GET /game/state → party  (or null)
 *   myUserId       — current viewer's user_id
 *   onInviteClick  — () => void   Open the party lobby to share the code
 */
export default function PartyBar({ party, myUserId, onInviteClick }) {
  const [buying, setBuying] = useState(false);
  if (!party) return null;

  const isHost = myUserId === party.host_user_id;
  const bothPresent = Boolean(party.host_user_id && party.guest_user_id);
  const myTurn = party.current_turn_user_id === myUserId;
  const otherName = isHost ? party.guest_display : party.host_display;

  const buyBoost = async () => {
    setBuying(true);
    try {
      const res = await api.post("/store/checkout", {
        product_key: "party_boost",
        origin_url: window.location.origin,
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("The Merchant took your offering but gave no path back.");
        setBuying(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Boost purchase failed");
      setBuying(false);
    }
  };

  return (
    <div
      className="panel px-4 py-2 flex items-center justify-between gap-3 mb-3"
      style={{
        borderColor: myTurn ? "rgba(214,158,71,0.7)" : "rgba(217,101,52,0.4)",
        background: myTurn ? "linear-gradient(90deg, rgba(90,60,15,0.35), rgba(20,10,8,0.6))" : undefined,
      }}
      data-testid="party-bar"
    >
      <div className="flex items-center gap-2">
        <Users size={14} className="gold-text" />
        <span className="text-[10px] tracking-[0.35em] blood-text">PARTY</span>
        <span className="text-xs ash-text">·</span>
        <span className="text-xs gold-text truncate" data-testid="party-host-name">
          {party.host_display || "Host"}{isHost && " (you)"}
        </span>
        <span className="text-xs ash-text">+</span>
        {party.guest_user_id ? (
          <span className="text-xs gold-text truncate" data-testid="party-guest-name">
            {party.guest_display || "Ally"}{!isHost && " (you)"}
          </span>
        ) : (
          <span className="text-xs ash-text italic">waiting for ally...</span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Party Boost purchase / status pill */}
        {bothPresent && (
          party.boost_active ? (
            <span
              className="text-[10px] tracking-[0.3em] flex items-center gap-1 px-2 py-0.5 rounded border"
              style={{
                color: party.boost_bonus_life ? "#f5c56b" : "#a0908a",
                borderColor: party.boost_bonus_life ? "rgba(214,158,71,0.7)" : "rgba(160,144,138,0.3)",
                background: party.boost_bonus_life ? "rgba(90,60,15,0.35)" : "transparent",
              }}
              data-testid="party-boost-pill"
              title={party.boost_bonus_life ? "Shared bonus life active" : "Party Boost consumed"}
            >
              <Flame size={11} /> {party.boost_bonus_life ? "BOOSTED" : "BOOST SPENT"}
            </span>
          ) : (
            <button
              onClick={buyBoost}
              disabled={buying}
              className="text-[10px] tracking-[0.3em] px-2 py-0.5 rounded border border-[#d69e47]/60 hover:bg-[#d69e47]/20 transition-colors flex items-center gap-1 gold-text disabled:opacity-50"
              data-testid="party-boost-buy-btn"
              title="Party Boost: shared bonus life + +25% crit — $4.99"
            >
              {buying ? <Loader2 className="animate-spin" size={11} /> : <Flame size={11} />}
              BOOST · $4.99
            </button>
          )
        )}

        {bothPresent ? (
          myTurn ? (
            <span className="text-[10px] tracking-[0.3em] gold-text flex items-center gap-1 animate-pulse" data-testid="party-turn-yours">
              <Sword size={12} /> YOUR TURN
            </span>
          ) : (
            <span className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1" data-testid="party-turn-waiting">
              <Loader2 className="animate-spin" size={12} />
              {otherName || "Ally"}&apos;S TURN
            </span>
          )
        ) : (
          isHost && (
            <button
              onClick={onInviteClick}
              className="btn-glow text-[10px] tracking-[0.3em] px-3 py-1"
              data-testid="party-bar-invite-btn"
            >
              SHOW CODE
            </button>
          )
        )}
      </div>
    </div>
  );
}
